import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SubscriptionConfig {
  table: string;
  event?: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  filter?: string;
  schema?: string;
}

interface SubscriptionOptions {
  enabled?: boolean;
  debounceMs?: number;
  priority?: 'high' | 'medium' | 'low';
  onError?: (error: any) => void;
}

class RealtimeManager {
  private static instance: RealtimeManager | null = null;
  private subscriptions = new Map<string, any>();
  private callbacks = new Map<string, Set<(payload: any) => void>>();
  private debounceTimers = new Map<string, NodeJS.Timeout>();
  private connectionHealthy = true;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private isInitialized = false;

  static getInstance(): RealtimeManager {
    try {
      if (!this.instance) {
        this.instance = new RealtimeManager();
        this.instance.initialize();
      }
      return this.instance;
    } catch (error) {
      console.error('❌ Failed to create RealtimeManager instance:', error);
      // Return a safe fallback instance
      if (!this.instance) {
        this.instance = new RealtimeManager();
      }
      return this.instance;
    }
  }

  private initialize() {
    try {
      this.isInitialized = true;
      console.log('✅ RealtimeManager initialized');
    } catch (error) {
      console.error('❌ Failed to initialize RealtimeManager:', error);
      this.isInitialized = false;
    }
  }

  private generateChannelKey(config: SubscriptionConfig): string {
    return `${config.schema || 'public'}.${config.table}.${config.event || '*'}.${config.filter || ''}`;
  }

  subscribe(
    config: SubscriptionConfig,
    callback: (payload: any) => void,
    options: SubscriptionOptions = {}
  ): () => void {
    if (!this.isInitialized) {
      console.warn('⚠️ RealtimeManager not initialized, skipping subscription');
      return () => {};
    }

    const key = this.generateChannelKey(config);
    const { enabled = true, debounceMs = 0, priority = 'medium', onError } = options;

    if (!enabled) {
      return () => {}; // Return empty cleanup function
    }

    try {
      // Add callback to the set for this subscription
      if (!this.callbacks.has(key)) {
        this.callbacks.set(key, new Set());
      }
      this.callbacks.get(key)!.add(callback);

      // Create subscription if it doesn't exist
      if (!this.subscriptions.has(key)) {
        this.createSubscription(config, key, debounceMs, onError);
      }

      // Return cleanup function
      return () => {
        this.unsubscribe(key, callback);
      };
    } catch (error) {
      console.error('❌ Error in subscribe:', error);
      if (onError) {
        onError(error);
      }
      return () => {};
    }
  }

  private createSubscription(
    config: SubscriptionConfig,
    key: string,
    debounceMs: number,
    onError?: (error: any) => void
  ) {
    try {
      const channelName = `realtime-${key}-${Date.now()}`;
      
      const channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes' as any,
          {
            event: config.event || '*',
            schema: config.schema || 'public',
            table: config.table,
            ...(config.filter && { filter: config.filter })
          },
          (payload) => {
            this.handlePayload(key, payload, debounceMs);
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            this.connectionHealthy = true;
            this.reconnectAttempts = 0;
            console.log(`✅ Realtime subscription active: ${key}`);
          } else if (status === 'CHANNEL_ERROR') {
            this.connectionHealthy = false;
            this.handleConnectionError(key, config, debounceMs, onError);
          }
        });

      this.subscriptions.set(key, channel);
    } catch (error) {
      console.error(`❌ Failed to create realtime subscription: ${key}`, error);
      if (onError) {
        onError(error);
      }
    }
  }

  private handlePayload(key: string, payload: any, debounceMs: number) {
    const callbacks = this.callbacks.get(key);
    if (!callbacks || callbacks.size === 0) return;

    const executeCallbacks = () => {
      callbacks.forEach(callback => {
        try {
          callback(payload);
        } catch (error) {
          console.error(`❌ Error in realtime callback for ${key}:`, error);
        }
      });
    };

    if (debounceMs > 0) {
      // Clear existing timer
      if (this.debounceTimers.has(key)) {
        clearTimeout(this.debounceTimers.get(key)!);
      }

      // Set new timer
      const timer = setTimeout(executeCallbacks, debounceMs);
      this.debounceTimers.set(key, timer);
    } else {
      executeCallbacks();
    }
  }

  private handleConnectionError(
    key: string,
    config: SubscriptionConfig,
    debounceMs: number,
    onError?: (error: any) => void
  ) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
      
      console.warn(`🔄 Attempting to reconnect realtime subscription ${key} in ${delay}ms (attempt ${this.reconnectAttempts})`);
      
      setTimeout(() => {
        if (this.callbacks.has(key) && this.callbacks.get(key)!.size > 0) {
          this.cleanupSubscription(key);
          this.createSubscription(config, key, debounceMs, onError);
        }
      }, delay);
    } else {
      console.error(`❌ Max reconnection attempts reached for ${key}`);
      if (onError) {
        onError(new Error(`Failed to maintain realtime connection for ${key}`));
      }
    }
  }

  private unsubscribe(key: string, callback: (payload: any) => void) {
    const callbacks = this.callbacks.get(key);
    if (callbacks) {
      callbacks.delete(callback);
      
      // If no more callbacks, cleanup the subscription
      if (callbacks.size === 0) {
        this.cleanupSubscription(key);
      }
    }
  }

  private cleanupSubscription(key: string) {
    // Clear debounce timer
    if (this.debounceTimers.has(key)) {
      clearTimeout(this.debounceTimers.get(key)!);
      this.debounceTimers.delete(key);
    }

    // Remove subscription
    const subscription = this.subscriptions.get(key);
    if (subscription) {
      try {
        supabase.removeChannel(subscription);
      } catch (error) {
        console.warn(`Warning: Failed to remove channel for ${key}:`, error);
      }
      this.subscriptions.delete(key);
    }

    // Remove callbacks
    this.callbacks.delete(key);
  }

  // Public method to check connection health
  isHealthy(): boolean {
    return this.isInitialized && this.connectionHealthy;
  }

  // Public method to get active subscription count
  getActiveSubscriptionCount(): number {
    return this.isInitialized ? this.subscriptions.size : 0;
  }

  // Public method to cleanup all subscriptions (for app cleanup)
  cleanup() {
    if (!this.isInitialized) {
      return;
    }

    try {
      Array.from(this.subscriptions.keys()).forEach(key => {
        this.cleanupSubscription(key);
      });
    } catch (error) {
      console.error('❌ Error during cleanup:', error);
    }
  }
}

export const useRealtimeManager = () => {
  const managerRef = useRef<RealtimeManager | null>(null);

  // Initialize manager safely
  const getManager = useCallback(() => {
    if (!managerRef.current) {
      try {
        managerRef.current = RealtimeManager.getInstance();
      } catch (error) {
        console.error('❌ Failed to get RealtimeManager instance:', error);
        return null;
      }
    }
    return managerRef.current;
  }, []);

  const subscribe = useCallback(
    (
      config: SubscriptionConfig,
      callback: (payload: any) => void,
      options?: SubscriptionOptions
    ) => {
      const manager = getManager();
      if (!manager) {
        console.warn('⚠️ RealtimeManager not available for subscription');
        return () => {};
      }
      
      try {
        return manager.subscribe(config, callback, options);
      } catch (error) {
        console.error('❌ Error in useRealtimeManager.subscribe:', error);
        return () => {};
      }
    },
    [getManager]
  );

  const isHealthy = useCallback(() => {
    const manager = getManager();
    if (!manager) {
      return false;
    }
    
    try {
      return manager.isHealthy();
    } catch (error) {
      console.error('❌ Error checking health:', error);
      return false;
    }
  }, [getManager]);

  const getActiveSubscriptionCount = useCallback(() => {
    const manager = getManager();
    if (!manager) {
      return 0;
    }
    
    try {
      return manager.getActiveSubscriptionCount();
    } catch (error) {
      console.error('❌ Error getting subscription count:', error);
      return 0;
    }
  }, [getManager]);

  const cleanup = useCallback(() => {
    const manager = getManager();
    if (!manager) {
      return;
    }
    
    try {
      manager.cleanup();
    } catch (error) {
      console.error('❌ Error during cleanup:', error);
    }
  }, [getManager]);

  return {
    subscribe,
    isHealthy,
    getActiveSubscriptionCount,
    cleanup
  };
};
